// Referral engine UI — settings + queue of pending payouts + history.
import React, { useEffect, useState } from 'react';
import { G, styles, api, fmtUsd } from '../theme.js';
import { Btn, Badge, Spinner, Input, Field } from '../ui.jsx';

export function Referrals({ toast }) {
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [tab, setTab] = useState('pending');

  const load = () => {
    api('/api/referrals').then(setData).catch(()=>{});
    api('/api/referrals/settings').then(setSettings).catch(()=>{});
  };
  useEffect(() => { load(); }, []);

  const saveSettings = async () => {
    try {
      await api('/api/referrals/settings', { method:'PATCH', body: settings });
      toast('Saved');
    } catch (e) { toast(e.message, 'error'); }
  };

  const pay = async (id) => {
    try { await api(`/api/referrals/${id}/pay`, { method:'POST' }); load(); toast('Marked paid'); }
    catch (e) { toast(e.message, 'error'); }
  };

  if (!data || !settings) return <div style={{ display:'flex', justifyContent:'center', padding:80 }}><Spinner sz={32}/></div>;

  const filtered = tab === 'all' ? data.referrals : data.referrals.filter(r => r.status === tab);

  return <div style={{ padding:24 }}>
    <h1 style={{ margin:0, fontSize:24, fontWeight:800 }}>Referrals</h1>
    <div style={{ color:G.textMuted, fontSize:14, marginTop:6, marginBottom:18 }}>
      Reward existing customers for bringing in new ones. Single biggest growth lever for trades.
    </div>

    {/* Settings card */}
    <div style={{ ...styles.card, padding:18, marginBottom:18 }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
        <strong style={{ fontSize:15 }}>Program settings</strong>
        <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, cursor:'pointer' }}>
          <input type="checkbox" checked={!!settings.enabled} onChange={e=>setSettings({...settings, enabled:e.target.checked})}/>
          {settings.enabled ? <span style={{ color:G.green, fontWeight:700 }}>Enabled</span> : <span style={{ color:G.textMuted }}>Disabled</span>}
        </label>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
        <Field label="Reward per converted referral">
          <Input type="number" step="1" value={(settings.rewardCents||0)/100}
            onChange={e=>setSettings({...settings, rewardCents: Math.round(Number(e.target.value)*100)})}/>
        </Field>
        <Field label="Minimum job total to convert">
          <Input type="number" step="1" value={(settings.minJobCents||0)/100}
            onChange={e=>setSettings({...settings, minJobCents: Math.round(Number(e.target.value)*100)})}/>
        </Field>
      </div>
      <div style={{ display:'flex', justifyContent:'flex-end' }}><Btn v="primary" onClick={saveSettings}>Save</Btn></div>
    </div>

    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))', gap:12, marginBottom:18 }}>
      <Stat label="Pending" value={data.summary.pending||0} sub="not yet converted" color={G.cyan}/>
      <Stat label="Converted (owed)" value={fmtUsd((data.summary.owedCents||0)/100)} sub={`${data.summary.converted||0} customer${data.summary.converted===1?'':'s'}`} color={G.yellow}/>
      <Stat label="Paid Out" value={data.summary.paid||0} sub="historical" color={G.green}/>
    </div>

    <div style={{ display:'flex', gap:6, marginBottom:14, background:G.input, padding:4, borderRadius:10, width:'fit-content' }}>
      {['pending','converted','paid','all'].map(s => (
        <Btn key={s} sz="sm" v={tab===s?'primary':'ghost'} onClick={()=>setTab(s)}>
          {s.charAt(0).toUpperCase()+s.slice(1)}
        </Btn>
      ))}
    </div>

    {filtered.length === 0 ? (
      <div style={{ ...styles.card, padding:48, textAlign:'center', color:G.textMuted }}>
        Nothing here yet. Share each customer's referral link from the contact profile to start tracking.
      </div>
    ) : <div style={{ ...styles.card, padding:0, overflow:'hidden' }}>
      <table style={{ width:'100%', borderCollapse:'collapse' }}>
        <thead><tr>{['Referrer','Referred','Status','Reward','Created','Converted',''].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr></thead>
        <tbody>{filtered.map(r => (
          <tr key={r.id}>
            <td style={{ ...styles.td, fontWeight:600 }}>{r.referrerName||'—'}</td>
            <td style={styles.td}>{r.referredName||'—'}</td>
            <td style={styles.td}>
              <Badge c={r.status==='converted'?G.yellow:r.status==='paid'?G.green:G.cyan} bg={(r.status==='converted'?G.yellow:r.status==='paid'?G.green:G.cyan)+'22'}>{r.status}</Badge>
            </td>
            <td style={{ ...styles.td, fontWeight:700, color:G.green }}>{fmtUsd((r.rewardCents||0)/100)}</td>
            <td style={{ ...styles.td, fontSize:12, color:G.textMuted }}>{new Date(r.createdAt).toLocaleDateString()}</td>
            <td style={{ ...styles.td, fontSize:12, color:G.textMuted }}>{r.convertedAt ? new Date(r.convertedAt).toLocaleDateString() : '—'}</td>
            <td style={styles.td}>{r.status === 'converted' && <Btn sz="xs" v="primary" onClick={()=>pay(r.id)}>Mark Paid</Btn>}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>}
  </div>;
}

function Stat({ label, value, sub, color }) {
  return <div style={{ ...styles.card, borderColor: color+'66' }}>
    <div style={{ fontSize:11, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em' }}>{label}</div>
    <div style={{ fontSize:22, fontWeight:800, color, marginTop:6 }}>{value}</div>
    <div style={{ fontSize:12, color:G.textMuted, marginTop:4 }}>{sub}</div>
  </div>;
}
